import type { Component } from "solid-js";
import { ErrorBoundary, Show } from "solid-js";
import { useTranslation } from "../state/i18n";
import InlineMessage from "../components/inlineMessage/InlineMessage";
import { directUserToSupport } from "./supportUtil";
import { getCurrentScope, withSentryErrorBoundary } from "@sentry/solid";
import environment from "../environment";

// A function that wraps any component with an error boundary that displays a user-friendly message to the user and sends
// the error to Sentry.
const withErrorBoundary: <P extends object>(WrappedComponent: Component<P>, Fallback?: Component<P>) => Component<P> =
  (WrappedComponent, Fallback) => (props) => {
    const SentryErrorBoundary = withSentryErrorBoundary(ErrorBoundary);
    return (
      <SentryErrorBoundary
        fallback={(e) => {
          if (environment.isLocal()) {
            // oxlint-disable-next-line no-console -- Should never be sent to Sentry
            console.error(e);
          }

          const { t } = useTranslation();

          return (
            <Show
              when={Fallback}
              fallback={
                <InlineMessage
                  variant="warning"
                  action={{
                    label: t(($) => $.COMPONENT_ERROR_BOUNDARY_ACTIONS_CONTACT_SUPPORT_LABEL),
                    onClick: () => directUserToSupport(),
                  }}
                >
                  <p class="text-base-medium">{t(($) => $.COMPONENT_ERROR_BOUNDARY_TITLE)}</p>
                  <p class="text-base-regular">Trace ID: {getCurrentScope().getPropagationContext().traceId}</p>
                </InlineMessage>
              }
            >
              {(Fallback) => Fallback()(props)}
            </Show>
          );
        }}
      >
        <WrappedComponent {...props} />
      </SentryErrorBoundary>
    );
  };

export default withErrorBoundary;
